import { Component, ErrorInfo, ReactNode } from 'react';

/**
 * Props for ErrorBoundary component
 */
export interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Fallback UI to display when an error occurs, or render function with reset capability */
  fallback: ReactNode | ((props: { resetError: () => void }) => ReactNode);
}

/**
 * State for ErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * ErrorBoundary component for catching and displaying React errors.
 *
 * Wraps child components and displays a fallback UI when an error occurs
 * during rendering, in a lifecycle method, or in constructors of the whole tree below.
 *
 * Features:
 * - Catches JavaScript errors in child component tree
 * - Displays custom fallback UI on error
 * - Logs error details to console for debugging
 *
 * Usage:
 * ```tsx
 * // Simple fallback (no reset)
 * <ErrorBoundary
 *   fallback={
 *     <div className="error-container">
 *       <h2>Something went wrong</h2>
 *       <p>Please refresh the page or contact support.</p>
 *     </div>
 *   }
 * >
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * // With reset capability
 * <ErrorBoundary
 *   fallback={({ resetError }) => (
 *     <div className="error-container">
 *       <h2>Something went wrong</h2>
 *       <button onClick={resetError}>Try Again</button>
 *     </div>
 *   )}
 * >
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * Common patterns:
 * - Wrap Suspense boundaries for combined loading/error handling
 * - Use at route level for page-level error handling
 * - Use around individual features for granular error isolation
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    // Update state so next render shows fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details for debugging

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  /**
   * Reset error state to allow retry
   * Useful when the error might be transient (network issues, etc.)
   */
  resetError = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;
      // Support both static fallback and render function with resetError
      if (typeof fallback === 'function') {
        return fallback({ resetError: this.resetError });
      }
      return fallback;
    }

    return this.props.children;
  }
}
